import { useCallback, useEffect, useMemo } from 'react';

import { useRouter } from 'expo-router';
import { StyleSheet, Text, View } from 'react-native';

import ActionButton, { ActionButtonVariants } from 'components/action-button';
import { CardModal } from 'components/card-modal';
import LoyaltyPointsMedal from 'components/icons/loyalty-points-medal';
import YouRuleLogo from 'components/you-rule-logo';
import { t } from 'state/intl/translations';
import { useLoyaltyContext } from 'state/loyalty';
import { primitive, spacing } from 'styles/constants';

const ChallengeCompleted = () => {
  const router = useRouter();

  const { challengeCompletedModalData } = useLoyaltyContext();
  const { monthlyChallengePoints = 0, profileChallengePoints = 0 } = challengeCompletedModalData;

  const handleClose = useCallback(() => {
    router.dismiss();
  }, [router]);

  const ctaButtonLabel = t('gotIt');

  const totalPointsEarned = monthlyChallengePoints + profileChallengePoints;

  const descriptionMessage = useMemo(() => {
    const hasMonthly = monthlyChallengePoints > 0;
    const hasProfile = profileChallengePoints > 0;

    if (hasMonthly && hasProfile) {
      return t('youCompletedYourMonthlyAndProfileChallenges');
    }

    if (hasMonthly) {
      return t('youCompletedYourMonthlyChallenge');
    }

    return t('youCompletedYourProfileChallenge');
  }, [monthlyChallengePoints, profileChallengePoints]);

  useEffect(() => {
    if (!monthlyChallengePoints && !profileChallengePoints) {
      handleClose();
    }
  }, [handleClose, monthlyChallengePoints, profileChallengePoints]);

  return (
    <CardModal
      alwaysShowHeader
      webAnimate
      standardPadding
      customHeaderCenter={
        <View style={{ paddingTop: 10 }}>
          <YouRuleLogo />
        </View>
      }
      headerFlexStartStyleOverride={{ alignItems: 'center' }}
      headerFlexEndStyleOverride={{ flex: 0 }}
      wrappedChildrenStyle={{ backgroundColor: primitive.$mayoEggWhite }}
      onDismiss={handleClose}
      footer={
        <ActionButton
          fullWidth
          accessibilityRole="link"
          onPress={handleClose}
          variant={ActionButtonVariants.OUTLINE}
          accessibilityLabel={ctaButtonLabel}
        >
          {ctaButtonLabel}
        </ActionButton>
      }
    >
      <View style={styles.container}>
        <LoyaltyPointsMedal points={totalPointsEarned} size={180} />
        <Text style={styles.title}>{t('youDidIt', 'You Did It!')}</Text>
        <Text style={styles.description}>{descriptionMessage}</Text>
        <Text style={styles.caption}>
          {t('crownsAllYours', {
            points: totalPointsEarned,
            defaultMessage: `That's {points} Crowns — all yours!`,
          })}
        </Text>
      </View>
    </CardModal>
  );
};

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: spacing.$11,
  },
  title: {
    fontFamily: primitive.fontFamily.$headers,
    fontSize: primitive.fontSizes.xl,
    marginTop: spacing.$7,
  },
  description: {
    fontSize: primitive.fontSizes.md,
    fontFamily: primitive.fontFamily.$body,
    marginTop: spacing.$2,
    lineHeight: primitive.fontSizes.md * 1.4,
    textAlign: 'center',
  },
  caption: {
    fontSize: primitive.fontSizes.sm,
    fontFamily: primitive.fontFamily.$body,
    marginTop: spacing.$9,
    textAlign: 'center',
  },
});

export default ChallengeCompleted;
